/**
 * `<caos-segmented-filter>` — a segmented control of mutually exclusive quick filters
 * (e.g. All · Active · Mine). (Catalog: Views & filtering › SegmentedFilter — a row of mutually exclusive quick filters, one of them in force.)
 *
 * Segments come from the `options` property (`{ value, label }[]` or plain strings) or
 * declarative `<button>` / `<option>` children; `value` is the single active segment.
 * The active segment carries the accent (design system: "active tab carries the
 * accent"), which moves with a brand override. Keyboard: the group is a roving-tabindex
 * toolbar — only the active segment is tabbable, and Arrow Left/Right/Home/End move and
 * activate. Each segment is a `<button aria-pressed>`; a bubbling, composed `change`
 * fires on selection. Styling seams: `::part(group)`, `::part(segment)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { applyAccessibleName } from '../accessible-name.js';

export interface CaosSegmentOption {
  value: string;
  label: string;
}

const sheet = makeSheet(`
  :host { display: inline-flex; max-width: 100%; }

  .group {
    display: inline-flex;
    align-items: stretch;
    padding: 3px;
    gap: 2px;
    background: var(--caos-color-surface-2);
    border: 1px solid var(--caos-color-border);
    border-radius: 3px;
    max-width: 100%;
    overflow-x: auto;
    scrollbar-width: none;
  }
  .group::-webkit-scrollbar { display: none; }

  .segment {
    appearance: none;
    border: 1px solid transparent;
    background: transparent;
    color: var(--caos-color-text-muted);
    /* Dragon SegmentedFilter — mono, 10px, .12em, uppercase */
    font-family: var(--caos-font-mono);
    font-size: 10px;
    font-weight: var(--caos-font-weight-label);
    letter-spacing: 0.12em;
    text-transform: uppercase;
    line-height: 1;
    padding: 6px 12px;
    border-radius: var(--caos-radius-sm);
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.12s ease, color 0.12s ease;
  }
  .segment:hover:not([aria-pressed="true"]) { color: var(--caos-color-text); }
  .segment[aria-pressed="true"] {
    /* the active segment = solid accent fill + the ink drawn on it (Dragon solid-red active) */
    /* CAOS-1551 — accent-fill under white text: white on accent is 3.60:1, below AA. */
    background: var(--caos-color-accent-fill);
    color: var(--caos-color-accent-contrast);
    border-color: var(--caos-color-accent-fill);
  }
  .segment:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
`);

export class CaosSegmentedFilter extends CaosElement {
  static get observedAttributes(): string[] {
    return ['value', 'label', 'aria-label'];
  }

  private group: HTMLDivElement;
  private _options: CaosSegmentOption[] | null = null;
  private segments: HTMLButtonElement[] = [];

  constructor() {
    super(sheet);
    this.group = document.createElement('div');
    this.group.className = 'group';
    this.group.setAttribute('part', 'group');
    this.group.setAttribute('role', 'group');
    this.group.addEventListener('keydown', (e) => this.onKeydown(e));
    this.root.append(this.group);
  }

  connectedCallback(): void {
    if (this._options === null) this.readLightDom();
    this.syncName();
    this.render();
  }

  attributeChangedCallback(name: string): void {
    // CAOS-648 — the name follows whichever attribute it is written in, whenever that changes.
    if (name === 'label' || name === 'aria-label') {
      this.syncName();
      return;
    }
    this.reflectActive();
  }

  /**
   * CAOS-1572 — the accessible name, read from `label` first and from `aria-label` after it.
   *
   * `label` exists because a stored placement carries a component's own inputs and nothing else. A
   * page assembled from metadata can set an input; it cannot set an element attribute no component
   * declares. So the name this control's meaning depends on — Scope, Period, Record facets — was
   * unsayable on a real page, while the documentation showed an example setting `aria-label` that a
   * page could not copy. Taking the name from a declared `label` is the shape `data-table.ts`
   * already uses for the grid, so the input is the library's existing answer rather than a second one.
   *
   * It goes one step further than the grid, and the step is deliberate rather than an oversight: the
   * grid reads `label` ALONE, while these two go on reading `aria-label` after it. The grid never
   * documented any other way to name itself; these two did, the workspace's own record page, shell
   * frame and component reference all set it, and every piece of hand-written markup reaches for it
   * first. `label` wins where both are present, because the declared input is the one a page can
   * author, so it is the one that decides.
   */
  private syncName(): void {
    applyAccessibleName(this.group, this.getAttribute('label') ?? this.getAttribute('aria-label'), 'Filter');
  }

  /** The segments (data in). */
  get options(): CaosSegmentOption[] {
    return this._options ?? [];
  }
  set options(list: Array<CaosSegmentOption | string>) {
    this._options = list.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
    this.render();
  }

  get value(): string {
    return this.getAttribute('value') ?? '';
  }
  set value(v: string) {
    this.setAttribute('value', v);
  }

  /** Read declarative `<button>` / `<option>` children as the segment source. */
  private readLightDom(): void {
    const nodes = Array.from(this.children).filter(
      (c) => c.tagName === 'BUTTON' || c.tagName === 'OPTION',
    );
    if (nodes.length) {
      this._options = nodes.map((n) => ({
        value: (n as HTMLOptionElement).value || (n.textContent ?? '').trim(),
        label: (n.textContent ?? '').trim(),
      }));
    }
  }

  private render(): void {
    const opts = this._options ?? [];
    this.group.replaceChildren();
    this.segments = opts.map((o) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'segment';
      btn.setAttribute('part', 'segment');
      btn.dataset.value = o.value;
      btn.textContent = o.label;
      btn.addEventListener('click', () => this.select(o.value, true));
      this.group.append(btn);
      return btn;
    });
    // Default the active segment to the current value or the first option.
    const first = opts[0];
    if (!this.hasAttribute('value') && first) {
      this.setAttribute('value', first.value);
    }
    this.reflectActive();
  }

  private reflectActive(): void {
    const active = this.getAttribute('value');
    this.segments.forEach((btn) => {
      const isActive = btn.dataset.value === active;
      btn.setAttribute('aria-pressed', String(isActive));
      // roving tabindex — only the active segment is in the tab order
      btn.tabIndex = isActive ? 0 : -1;
    });
  }

  private select(value: string, focus: boolean): void {
    if (this.getAttribute('value') !== value) {
      this.setAttribute('value', value);
      this.dispatchEvent(new CustomEvent('change', { bubbles: true, composed: true, detail: { value } }));
    }
    if (focus) {
      const btn = this.segments.find((b) => b.dataset.value === value);
      btn?.focus();
    }
  }

  private onKeydown(e: KeyboardEvent): void {
    const keys = ['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
    if (!keys.includes(e.key) || !this.segments.length) return;
    e.preventDefault();
    const current = this.segments.findIndex((b) => b.dataset.value === this.getAttribute('value'));
    const last = this.segments.length - 1;
    let next = current < 0 ? 0 : current;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = current >= last ? 0 : current + 1;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = current <= 0 ? last : current - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
    }
    const value = this.segments[next]?.dataset.value;
    if (value !== undefined) this.select(value, true);
  }
}

define('caos-segmented-filter', CaosSegmentedFilter);

declare global {
  interface HTMLElementTagNameMap {
    'caos-segmented-filter': CaosSegmentedFilter;
  }
}
